// Reorganized modes into 5 main categories - Title Case
// Following Swiss Medical design principles

export const MODE_CATEGORIES = [
  {
    id: 'clinical',
    name: 'Clinical',
    description: 'Diagnosis, Emergencies, Treatment Protocols',
    modes: [
      { id: 'general', label: 'General Query' },
      { id: 'emergency', label: 'Emergency Protocols' },
      { id: 'opd', label: 'OPD Consultation' },
      { id: 'differential', label: 'Differential Diagnosis' },
      { id: 'drug-interactions', label: 'Drug Interactions' },
      { id: 'indian-drugs', label: 'Indian Drug Database' },
      { id: 'guidelines', label: 'Latest Guidelines' },
      { id: 'anatomy', label: 'Know The Anatomy' },
      { id: 'surgical', label: 'Surgical Procedures' },
      { id: 'lab-interpretation', label: 'Lab Interpretation' },
      { id: 'daily-updates', label: 'Daily Updates In OBGYN' },
      { id: 'case-discussion', label: 'Case Discussion' },
    ]
  },
  {
    id: 'patient',
    name: 'Patient Care',
    description: 'Counselling, Explanations, Follow-Ups',
    modes: [
      { id: 'counselling', label: 'Counselling Scripts' },
      { id: 'patient-explanation', label: 'Patient Explanation Generator' },
      { id: 'antenatal', label: 'Antenatal Care' },
      { id: 'infertility', label: 'Infertility Management' },
      { id: 'menopause', label: 'Menopause' },
      { id: 'ultrasound', label: 'Ultrasound Reports' },
      { id: 'follow-up', label: 'Follow-Up Messages' },
      { id: 'whatsapp', label: 'WhatsApp Templates' },
      { id: 'diet-plans', label: 'Diet Plans' },
      { id: 'lactation', label: 'Lactation Support' },
      { id: 'birth-plan', label: 'Birth Plan Counselling' },
      { id: 'postpartum', label: 'Postpartum Care' },
    ]
  },
  {
    id: 'documentation',
    name: 'Documentation',
    description: 'Discharge, Consent, Reports, Certificates',
    modes: [
      { id: 'discharge', label: 'Discharge Summary' },
      { id: 'consent', label: 'Consent Forms' },
      { id: 'referral', label: 'Referral Letters' },
      { id: 'medicolegal', label: 'Medicolegal Help' },
      { id: 'insurance', label: 'Insurance Claims' },
      { id: 'certificate', label: 'Medical Certificates' },
      { id: 'operative-notes', label: 'Operative Notes' },
      { id: 'prescription', label: 'Prescription Writer' },
      { id: 'death-summary', label: 'Death Summary' },
      { id: 'transfer-summary', label: 'Transfer Summary' },
      { id: 'clinical-notes', label: 'Clinical Notes' },
    ]
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'CME, Conferences, Research, Publications',
    modes: [
      { id: 'presentation', label: 'Presentation Maker' },
      { id: 'prepare-talk', label: 'Prepare a Talk' },
      { id: 'cme', label: 'Organize CME' },
      { id: 'conference', label: 'Organize Conference' },
      { id: 'research', label: 'Papers & Posters' },
      { id: 'article-chapter', label: 'Write An Article/Chapter' },
      { id: 'patient-comm', label: 'Patient Communication' },
      { id: 'exam-revision', label: 'PG Exam Revision' },
      { id: 'mrcog', label: 'MRCOG Prep' },
      { id: 'obgyn-conferences', label: 'OBGYN Conferences' },
      { id: 'ebooks', label: 'E-Books Library' },
      { id: 'thesis-help', label: 'Thesis Writing Help' },
      { id: 'grant-writing', label: 'Grant Proposal Writing' },
    ]
  },
  {
    id: 'specialty',
    name: 'Specialty',
    description: 'Subspeciality Areas',
    modes: [
      { id: 'obstetrics', label: 'Obstetrics' },
      { id: 'laparoscopy', label: 'Laparoscopy' },
      { id: 'hysteroscopy', label: 'Hysteroscopy' },
      { id: 'robotic-surgery', label: 'Robotic Surgery' },
      { id: 'gynec-cancers', label: 'Gynec Oncology' },
      { id: 'cosmetic', label: 'Cosmetic Gynecology' },
      { id: 'preventive', label: 'Preventive Care' },
      { id: 'adolescent', label: 'Adolescent Health' },
      { id: 'urogynaecology', label: 'Urogynaecology' },
      { id: 'reproductive', label: 'Reproductive Medicine' },
      { id: 'high-risk', label: 'High Risk Pregnancy' },
      { id: 'fetal-medicine', label: 'Fetal Medicine' },
    ]
  }
];

// Important OBGYN History for loading state - 100+ facts
export const OBGYN_HISTORY = [
  // Ancient History (Before 1800)
  "1500 BC: Ebers Papyrus contains earliest written records of gynecological treatments in ancient Egypt.",
  "400 BC: Hippocrates described prolapse of the uterus and recommended pomegranate pessaries.",
  "200 AD: Soranus of Ephesus wrote the first systematic treatise on gynecology and obstetrics.",
  "1500: Jacob Nufer performed the first documented cesarean section with mother surviving in Switzerland.",
  "1540: Andreas Vesalius published accurate anatomical drawings of female reproductive organs.",
  "1580: Ambrose Paré introduced podalic version for obstructed labor.",
  "1663: Hendrik van Deventer published first systematic study of pelvic anatomy in childbirth.",
  "1752: William Smellie standardized obstetric forceps and published 'Treatise on Midwifery'.",
  "1774: William Hunter published 'Anatomy of the Human Gravid Uterus' with detailed illustrations.",
  
  // 19th Century - Age of Discovery
  "1809: Ephraim McDowell performed first successful ovariotomy in Kentucky, USA.",
  "1817: James Blundell performed first successful human blood transfusion for postpartum hemorrhage.",
  "1842: Crawford Long used ether anesthesia for surgery, paving way for painless operations.",
  "1843: Oliver Wendell Holmes published paper on puerperal fever transmission by physicians.",
  "1846: William Morton demonstrated ether anesthesia at Massachusetts General Hospital.",
  "1847: James Young Simpson introduced chloroform anesthesia for childbirth in Edinburgh.",
  "1847: Ignaz Semmelweis demonstrated handwashing reduces maternal mortality by 90%.",
  "1853: Queen Victoria used chloroform during childbirth, popularizing obstetric anesthesia.",
  "1860: Gustav Adolf Michaelis measured pelvic dimensions for predicting labor outcomes.",
  "1861: Semmelweis published 'Etiology of Childbed Fever' - foundation of antiseptic obstetrics.",
  "1867: Joseph Lister introduced antiseptic surgery using carbolic acid.",
  "1872: Robert Lawson Tait performed first successful salpingectomy for ectopic pregnancy.",
  "1876: Eduardo Porro performed cesarean hysterectomy to prevent hemorrhage.",
  "1878: Robert Battey performed first oophorectomy for non-malignant disease.",
  "1879: Max Sänger developed modern cesarean technique with uterine suturing.",
  "1881: First successful cesarean hysterectomy by Porro in Italy.",
  "1882: Max Sänger introduced low transverse uterine incision for cesarean section.",
  "1884: Carl Credé introduced silver nitrate prophylaxis for neonatal ophthalmia.",
  "1895: Wilhelm Röntgen discovered X-rays, later used for pelvimetry.",
  "1898: Ernst Wertheim performed first radical hysterectomy for cervical cancer.",
  
  // Early 20th Century
  "1901: Georg Kelling performed first laparoscopic procedure using a cystoscope in dogs.",
  "1901: Karl Landsteiner discovered blood groups, making safe transfusions possible.",
  "1906: August von Wassermann developed test for syphilis diagnosis.",
  "1910: Howard Kelly pioneered modern gynecologic surgery at Johns Hopkins.",
  "1912: Isador Rubin developed tubal insufflation test for infertility.",
  "1913: Albert Döderlein identified lactobacilli as normal vaginal flora.",
  "1920: Selmar Aschheim and Bernhard Zondek developed first pregnancy test.",
  "1921: Hans Christian Jacobaeus performed first thoracoscopy in humans.",
  "1923: Edgar Allen and Edward Doisy isolated estrogen hormone.",
  "1928: Alexander Fleming discovered penicillin, revolutionizing infection treatment.",
  "1929: George Papanicolaou developed the Pap smear for cervical cancer screening.",
  "1930: John Rock demonstrated in vitro fertilization in mammals.",
  "1933: Evelyn Anderson published on progesterone's role in maintaining pregnancy.",
  "1934: Adolf Butenandt isolated progesterone in crystalline form.",
  "1935: Carl Clauberg introduced hysterosalpingography for infertility diagnosis.",
  "1937: Gruenwald introduced pH measurement for diagnosing rupture of membranes.",
  "1939: Gruenwald and Suarez first used electronic fetal monitoring.",
  
  // Mid 20th Century
  "1940: Karl Gruenwald introduced Nitrazine test for rupture of membranes.",
  "1944: John Rock and Miriam Menkin achieved first human IVF in laboratory.",
  "1946: Robert Gross performed first successful ductus arteriosus ligation.",
  "1950: Virginia Apgar developed Apgar score for newborn assessment.",
  "1952: Douglas Bevis diagnosed Rh disease using amniotic fluid analysis.",
  "1953: Ian Donald pioneered diagnostic ultrasound in obstetrics in Glasgow.",
  "1954: Joe Hinsey introduced combined spinal-epidural anesthesia.",
  "1956: Georgios Papanicolaou standardized the Pap smear classification.",
  "1958: Ian Donald published landmark paper on ultrasound in obstetrics.",
  "1960: First oral contraceptive pill 'Enovid' approved by FDA.",
  "1960: Raoul Palmer performed first laparoscopic tubal sterilization.",
  "1962: A.W. Liley performed first successful intrauterine blood transfusion.",
  "1963: Thomas Starzl performed first liver transplant (relevant for HELLP syndrome).",
  "1965: Palmer introduced laparoscopic tubal surgery techniques.",
  "1966: Henry Klapholz developed contraction stress test.",
  "1967: Patrick Steptoe performed first successful laparoscopic sterilization.",
  "1968: Edward Hon developed electronic fetal heart rate monitoring.",
  "1970: J.G. Thornton introduced biophysical profile for fetal assessment.",
  "1971: Judah Folkman proposed anti-angiogenesis for cancer treatment.",
  
  // Late 20th Century - Modern Era
  "1972: Saling introduced fetal scalp blood sampling for intrapartum monitoring.",
  "1973: Roe v. Wade legalized abortion in United States.",
  "1974: First commercially available ultrasound machines for obstetrics.",
  "1976: Patrick Steptoe and Robert Edwards achieved first successful IVF conception.",
  "1977: Per Lindstedt introduced vaginal ultrasound probe.",
  "1978: Louise Brown born - world's first IVF baby by Edwards and Steptoe.",
  "1980: Kurt Semm performed first laparoscopic appendectomy.",
  "1981: First reports of AIDS, impacting prenatal care protocols.",
  "1983: Kypros Nicolaides introduced nuchal translucency screening.",
  "1984: First successful embryo freezing and subsequent pregnancy.",
  "1985: Bruhat performed first laparoscopic treatment of ectopic pregnancy.",
  "1986: First gestational surrogacy arrangement completed successfully.",
  "1987: Phillipe Mouret performed first laparoscopic cholecystectomy.",
  "1988: Harry Reich performed first laparoscopic hysterectomy in Pennsylvania.",
  "1989: First successful preimplantation genetic diagnosis performed.",
  "1990: Human Genome Project launched, advancing genetic diagnosis.",
  "1991: Jacques Donnez performed first laparoscopic myomectomy.",
  "1992: First successful intracytoplasmic sperm injection (ICSI) by Palermo.",
  "1993: First comprehensive ACLS guidelines for pregnancy published.",
  "1994: FDA approved mifepristone for medical abortion in France.",
  "1995: Camran Nezhat advanced laparoscopic surgery for endometriosis.",
  "1996: Dolly the sheep cloned - implications for reproductive medicine.",
  "1997: First successful uterine artery embolization for fibroids.",
  "1998: First PGD for single gene disorders in clinical practice.",
  "1999: FIGO revised staging for gynecological cancers.",
  
  // 21st Century
  "2000: Da Vinci robotic surgery system approved for gynecologic procedures.",
  "2001: First robotic hysterectomy performed successfully.",
  "2002: WHI study changed HRT prescribing practices dramatically.",
  "2003: Human genome sequencing completed, advancing prenatal genetics.",
  "2004: First successful uterus transplant attempted in Saudi Arabia.",
  "2005: HPV vaccine development completed by Ian Frazer.",
  "2006: HPV vaccine (Gardasil) approved - first cancer-preventing vaccine.",
  "2007: cfDNA testing development began for non-invasive prenatal testing.",
  "2008: First commercial NIPT tests introduced for Down syndrome.",
  "2009: First successful pregnancy after uterus transplant from deceased donor.",
  "2010: Single-port laparoscopic surgery gains popularity in gynecology.",
  "2011: ARRIVE trial began, later changing induction practices.",
  "2012: ACOG updated cervical cancer screening guidelines.",
  "2013: NIPT widely adopted for prenatal aneuploidy screening.",
  "2014: First successful live birth after uterus transplant in Sweden.",
  "2015: WHO eliminated mother-to-child HIV transmission in Cuba.",
  "2016: Zika virus declared public health emergency affecting pregnancies.",
  "2017: First gene-edited human embryos created (controversial).",
  "2018: FIGO updated cervical cancer staging to include imaging.",
  "2019: First successful uterus transplant from living donor in US.",
  "2020: COVID-19 pandemic transformed prenatal care delivery worldwide.",
  "2021: WHO updated postpartum hemorrhage treatment guidelines.",
  "2022: First successful pregnancy from robot-assisted uterus transplant.",
  "2023: AI-assisted embryo selection becomes standard in IVF.",
  "2024: CRISPR gene editing trials expand for genetic diseases.",
];

// Get random history fact
export const getRandomHistoryFact = () => {
  return OBGYN_HISTORY[Math.floor(Math.random() * OBGYN_HISTORY.length)];
};

// Templates for each mode - Title Case
export const MODE_TEMPLATES = {
  // CLINICAL CATEGORY
  'general': [
    'What Is The Management Of PCOS?',
    'Explain Endometriosis Staging (rASRM)',
    'Treatment Options For Uterine Fibroids',
    'Differential Diagnosis For Chronic Pelvic Pain',
    'Management Of Abnormal Uterine Bleeding',
    'When To Suspect Adenomyosis?',
  ],
  'emergency': [
    'PPH Management Stepwise Protocol',
    'Eclampsia Management Algorithm',
    'Ruptured Ectopic - Immediate Steps',
    'Cord Prolapse Management',
    'Shoulder Dystocia - HELPERR Mnemonic',
    'Amniotic Fluid Embolism Protocol',
    'DIC In Obstetrics Management',
    'Uterine Inversion Correction',
  ],
  'opd': [
    'PCOS Patient With Irregular Cycles - Workup',
    'Heavy Menstrual Bleeding - Investigation',
    'Post-Menopausal Bleeding - Rule Out Malignancy',
    'Recurrent Pregnancy Loss Workup',
    'Primary Amenorrhea Evaluation',
    'Vaginal Discharge - Diagnosis Approach',
  ],
  'differential': [
    'Differential: Acute Pelvic Pain',
    'Differential: Adnexal Mass',
    'Differential: Abnormal Uterine Bleeding',
    'Differential: Lower Abdominal Pain In Pregnancy',
    'Differential: Postmenopausal Bleeding',
  ],
  'drug-interactions': [
    'Metformin Interactions In PCOS',
    'Warfarin And Hormonal Contraceptives',
    'Antiepileptics And OCP Efficacy',
    'Antibiotics Affecting Contraceptive Pills',
    'Magnesium Sulfate Drug Interactions',
  ],
  'indian-drugs': [
    'Indian Brands For Progesterone Support',
    'Mifepristone-Misoprostol Brands In India',
    'Anti-D Immunoglobulin Indian Brands',
    'Cervical Ripening Agents In India',
    'Indian GnRH Agonist Brands',
  ],
  'guidelines': [
    'Latest ACOG Guidelines On GDM',
    'FOGSI Guidelines On PCOS',
    'RCOG Green-Top Guidelines PPH',
    'WHO Recommendations Cesarean Section',
    'FIGO Staging Cervical Cancer',
  ],
  'anatomy': [
    'Blood Supply Of Uterus',
    'Pelvic Floor Anatomy Simplified',
    'Ovarian Ligaments And Relations',
    'Ureter Course - Surgical Importance',
    'Perineal Body Anatomy',
  ],
  'surgical': [
    'Steps Of Abdominal Hysterectomy',
    'Cesarean Section Technique Step By Step',
    'Laparoscopic Cystectomy Steps',
    'Myomectomy Techniques Comparison',
    'Cervical Cerclage Techniques',
  ],
  'lab-interpretation': [
    'Interpreting AMH Levels',
    'Triple Test Interpretation',
    'Thyroid Function In Pregnancy',
    'OGTT Interpretation For GDM',
    'CA-125 Levels Interpretation',
  ],
  'daily-updates': [
    'Latest ACOG Practice Bulletins',
    'Recent Cochrane Reviews In OBGYN',
    'New Drugs Approved For Gynecology',
    'Emerging Trends In MIS',
    'Updates In Fetal Medicine',
  ],
  'case-discussion': [
    'Case: 28F With Primary Infertility',
    'Case: Postmenopausal Bleeding Workup',
    'Case: Recurrent Miscarriage',
    'Case: Large Ovarian Cyst Management',
    'Case: Preeclampsia At 32 Weeks',
  ],

  // PATIENT CARE CATEGORY
  'counselling': [
    'PCOS Lifestyle Counselling Points',
    'Menopause HRT Benefits And Risks',
    'Infertility Treatment Options Explained',
    'Pregnancy Termination Counselling',
    'Contraception Counselling Framework',
  ],
  'patient-explanation': [
    'Explain PCOS To Patient Simply',
    'Explain Cesarean Section Procedure',
    'Explain IVF Process Step By Step',
    'Explain Menopause And Symptoms',
    'Explain Hysterectomy Options',
    'Explain Laparoscopy To Patient',
  ],
  'antenatal': [
    'First Trimester ANC Checklist',
    'Second Trimester Screening Tests',
    'Third Trimester Monitoring Plan',
    'High Risk Pregnancy Protocol',
    'GDM Diet And Monitoring',
    'Rh Negative Pregnancy Management',
  ],
  'infertility': [
    'Basic Infertility Workup Checklist',
    'Day 2 Hormonal Profile Interpretation',
    'IUI Protocol And Success Rates',
    'IVF Preparation Counselling',
    'Male Factor Evaluation',
    'Ovulation Induction Protocols',
  ],
  'menopause': [
    'HRT Initiation - When And How',
    'Non-Hormonal Options For Hot Flashes',
    'Osteoporosis Prevention Protocol',
    'Vaginal Atrophy Treatment Options',
    'Menopause And Cardiovascular Risk',
    'POI Management',
  ],
  'ultrasound': [
    'First Trimester Scan Report Template',
    'NT Scan - What To Look For',
    'Anomaly Scan Systematic Approach',
    'Ovarian Cyst Characterization',
    'Fibroid Mapping For Surgery',
    'Endometrial Assessment',
  ],
  'follow-up': [
    'Post Cesarean Follow-Up Message',
    'Post Hysterectomy Care Instructions',
    'IVF 2-Week Wait Support',
    'Miscarriage Follow-Up Message',
    'Post D&C Care Instructions',
  ],
  'whatsapp': [
    'Appointment Reminder Template',
    'Lab Report Ready Notification',
    'Post-Operative Care Instructions',
    'Medication Reminder Message',
    'Follow-Up Appointment Reminder',
  ],
  'diet-plans': [
    'PCOS Diet Plan With Indian Foods',
    'Pregnancy Diet Trimester-Wise',
    'GDM Meal Planning Guide',
    'Post-Operative Nutrition',
    'Menopause Bone Health Diet',
    'Fertility Boosting Foods',
  ],
  'lactation': [
    'Breastfeeding Initiation Support',
    'Low Milk Supply Management',
    'Mastitis Treatment Protocol',
    'Medications Safe In Breastfeeding',
    'Weaning Guidance',
  ],
  'meditation': [
    'Prenatal Relaxation Techniques',
    'Labor Preparation Visualization',
    'Stress Relief For Doctors',
    'Sleep Meditation For Pregnancy',
    'Anxiety Relief Before Surgery',
  ],
  'birth-plan': [
    'Normal Delivery Birth Plan Template',
    'VBAC Counselling And Plan',
    'Elective Cesarean Birth Plan',
    'Pain Management Options',
    'Cord Blood Banking Counselling',
  ],
  'postpartum': [
    'Postpartum Care Checklist',
    'Postpartum Depression Screening',
    'Contraception After Delivery',
    'Exercise After Cesarean',
    'When To Seek Emergency Care',
  ],

  // DOCUMENTATION CATEGORY
  'discharge': [
    'Post Cesarean Section Discharge Summary',
    'Normal Vaginal Delivery Discharge Summary',
    'Post Hysterectomy Discharge Summary',
    'Post Myomectomy Discharge Summary',
    'Post Ectopic Pregnancy Discharge Summary',
    'Post D&C Procedure Discharge Summary',
  ],
  'consent': [
    'Laparoscopic Surgery Consent',
    'Cesarean Section Consent',
    'Hysterectomy Consent - All Routes',
    'IVF Procedure Consent',
    'MTP Consent Form',
    'Blood Transfusion Consent',
  ],
  'referral': [
    'Referral To Maternal-Fetal Medicine',
    'Referral To Gynec Oncologist',
    'Referral For IVF Treatment',
    'Referral To Urogynecologist',
    'Emergency Referral Letter',
  ],
  'medicolegal': [
    'Informed Consent Documentation',
    'Adverse Event Documentation',
    'Expert Opinion For Court',
    'PCPNDT Compliance Checklist',
    'MTP Documentation Requirements',
    'Maternal Death Audit Format',
  ],
  'insurance': [
    'Pre-Authorization Hysterectomy',
    'Claim Letter Cesarean Section',
    'IVF Treatment Cost Breakdown',
    'Medical Necessity Letter Fibroid',
    'Emergency Cesarean Justification',
  ],
  'certificate': [
    'Fitness Certificate For Travel',
    'Maternity Leave Certificate',
    'Medical Fitness For Work',
    'Pregnancy Confirmation Certificate',
    'Sterilization Certificate',
  ],
  'operative-notes': [
    'Cesarean Section Operative Notes',
    'Laparoscopic Hysterectomy Notes',
    'Abdominal Myomectomy Notes',
    'Diagnostic Laparoscopy Findings',
    'Hysteroscopy Procedure Notes',
  ],
  'prescription': [
    'PCOS Treatment Prescription',
    'Postoperative Medications',
    'Antepartum Care Prescription',
    'Menopause HRT Prescription',
    'Emergency Contraception',
  ],
  'death-summary': [
    'Maternal Death Summary Format',
    'Stillbirth Summary Documentation',
    'Neonatal Death Summary',
  ],
  'transfer-summary': [
    'High Risk Pregnancy Transfer',
    'Transfer To Higher Center',
    'ICU Transfer Documentation',
  ],
  'clinical-notes': [
    'OPD Consultation Notes Template',
    'Daily Progress Notes Format',
    'Pre-Operative Assessment Notes',
  ],

  // PROFESSIONAL CATEGORY
  'presentation': [
    'PPH Management Presentation',
    'PCOS Overview Slides Outline',
    'Laparoscopy Basics For Beginners',
    'Cesarean Section Techniques',
    'Endometriosis Management Update',
  ],
  'cme': [
    'Laparoscopy Workshop Agenda',
    'CME Certificate Template',
    'Faculty Invitation Letter',
    'CME Credit Points Calculation',
    'Sponsorship Request Letter',
    'Post-CME Report Format',
  ],
  'conference': [
    'Scientific Program Template',
    'Call For Abstracts Format',
    'Sponsorship Tier Proposal',
    'Speaker Invitation Letter',
    'Abstract Submission Guidelines',
    'Conference Budget Template',
  ],
  'research': [
    'Research Proposal Outline',
    'Poster Presentation Template',
    'Abstract Writing Guide',
    'Case Report Format',
    'Ethics Committee Application',
  ],
  'article-chapter': [
    'Article Outline: PCOS Management',
    'Book Chapter: Laparoscopy Basics',
    'Review Article: Endometriosis',
    'Case Report Writing Guide',
    'Editorial Writing Format',
  ],
  'prepare-talk': [
    'Prepare a 15-min CME talk on PCOS — latest FOGSI 2024',
    'Prepare a 10-min talk for FOGSI state branch on PPH active management',
    'Prepare a 20-min talk for OB-GYN residents on Bishop score & induction',
    'Prepare a 30-min keynote for AICOG on Endometriosis surgery vs medical Rx',
    'Prepare a 7-min TEDx-style talk for women on PCOS lifestyle',
    'Prepare a 15-min talk for paediatricians on neonatal hypoglycaemia from GDM mothers',
    'Prepare a 20-min talk for nurses on intrapartum monitoring',
  ],
  'social-media': [
    'Social media for Instagram · Educational: PCOS — 3 things every woman should know',
    'Social media for WhatsApp · Patient-Friendly: How to track ovulation',
    'Social media for LinkedIn · Inspirational: My first cesarean as a resident',
    'Social media for Facebook · New Launch: Painless delivery service at our clinic',
    'Social media for Instagram · Awareness Day: World Menopause Day',
    'Social media for LinkedIn · Educational: ARRIVE trial — should we induce at 39 weeks?',
    'Social media for WhatsApp · Promotional: Free PCOS consultation week',
    'Social media for Instagram · Q&A: Top 5 questions about IVF',
  ],
  'patient-comm': [
    'Patient communication for In-clinic counselling: New PCOS diagnosis',
    'Patient communication for Bad-news breaking: First-trimester miscarriage',
    'Patient communication for WhatsApp follow-up: Post-LSCS day 3',
    'Patient communication for Pre-procedure briefing: Diagnostic hysteroscopy',
    'Patient communication for Discharge instructions: Normal vaginal delivery',
    'Patient communication for Bad-news breaking: Fetal anomaly on anomaly scan',
    'Patient communication for In-clinic counselling: Endometriosis diagnosis & options',
  ],
  'exam-revision': [
    'Bishop score components',
    'Partograph interpretation',
    'PCOS Rotterdam criteria',
    'Preeclampsia classification',
    'Endometriosis staging rASRM',
    'Cervical cancer FIGO staging',
    'AUB PALM-COEIN classification',
  ],
  'mrcog': [
    'Part 1: Anatomy MCQs Practice',
    'Part 1: Physiology Key Topics',
    'Part 2: SBA Question Practice',
    'Part 3: OSCE Station Types',
    'MRCOG Guidelines Summary',
    'High Yield Topics For MRCOG',
  ],
  'obgyn-conferences': [
    'AICOG Annual Conference Details',
    'FOGSI Upcoming Events',
    'International Conferences Calendar',
    'Subspecialty Society Meetings',
  ],
  'ebooks': [
    'Williams Obstetrics Key Chapters',
    'Te Linde Operative Gynecology',
    'RCOG Green-Top Guidelines',
    'Free Online OBGYN Resources',
  ],
  'thesis-help': [
    'Thesis Topic Selection Guide',
    'Literature Review Writing',
    'Methodology Section Template',
    'Statistical Analysis Guidance',
    'Discussion Section Writing',
  ],
  'grant-writing': [
    'Research Grant Proposal Format',
    'Budget Justification Writing',
    'Grant Agencies In India',
    'Successful Grant Examples',
  ],

  // SPECIALTY CATEGORY
  'obstetrics': [
    'Normal Labor Management Protocol',
    'Preterm Labor Prevention Strategies',
    'Multiple Pregnancy Monitoring',
    'Placenta Previa Management',
    'VBAC Selection Criteria',
    'Induction Of Labor Protocols',
    'Third Stage Management',
  ],
  'laparoscopy': [
    'Port Placement Techniques',
    'Entry Methods Comparison',
    'Complication Prevention',
    'Hysterectomy Step By Step',
    'Ovarian Cystectomy Technique',
    'Energy Sources In Laparoscopy',
    'Suturing Techniques',
  ],
  'hysteroscopy': [
    'Diagnostic Hysteroscopy Indications',
    'Polypectomy Technique',
    'Septum Resection Method',
    'Myomectomy - When And How',
    'Asherman Syndrome Management',
    'Distension Media Choices',
    'Office Hysteroscopy Setup',
  ],
  'robotic-surgery': [
    'Robotic Hysterectomy Advantages',
    'Robotic Myomectomy Technique',
    'Robotic Sacrocolpopexy Steps',
    'Console Setup And Docking',
    'Port Placement For Robotic Surgery',
    'Robotic Vs Laparoscopic Comparison',
    'Complications Management',
    'Learning Curve In Robotic Surgery',
  ],
  'gynec-cancers': [
    'Cervical Cancer Screening Protocol',
    'Ovarian Cancer Staging And Treatment',
    'Endometrial Cancer Workup',
    'HPV Vaccination Guidelines',
    'Colposcopy Interpretation',
    'Fertility Sparing Surgery Options',
  ],
  'cosmetic': [
    'Labiaplasty Indications',
    'Vaginal Rejuvenation Options',
    'Hymenoplasty Counselling',
    'Laser Treatments In Gynecology',
    'PRP Therapy Applications',
  ],
  'preventive': [
    'Cervical Cancer Prevention',
    'Breast Cancer Screening Guidelines',
    'Osteoporosis Prevention Protocol',
    'Preconception Counselling Checklist',
    'Vaccination Schedule For Women',
  ],
  'adolescent': [
    'Puberty Disorders Evaluation',
    'Primary Amenorrhea Workup',
    'PCOS In Adolescents',
    'Dysmenorrhea In Teenagers',
    'Contraception For Adolescents',
  ],
  'urogynaecology': [
    'Urinary Incontinence Evaluation',
    'Pelvic Organ Prolapse Staging',
    'Pessary Fitting Guide',
    'Surgical Options For POP',
    'Overactive Bladder Management',
  ],
  'reproductive': [
    'Ovulation Induction Protocols',
    'IUI Procedure Steps',
    'IVF Cycle Management',
    'Donor Egg/Sperm Counselling',
    'Fertility Preservation Options',
  ],
  'high-risk': [
    'GDM Management Protocol',
    'Preeclampsia Surveillance',
    'Rh Isoimmunization Management',
    'Previous Cesarean Pregnancy',
    'Cardiac Disease In Pregnancy',
  ],
  'fetal-medicine': [
    'First Trimester Screening',
    'NIPT Interpretation',
    'Fetal Anomaly Evaluation',
    'IUGR Monitoring Protocol',
    'Amniocentesis Counselling',
  ],
  'maternal-medicine': [],
};

// Get all modes as flat list for compatibility
export const getAllModes = () => {
  const allModes = [];
  MODE_CATEGORIES.forEach(category => {
    category.modes.forEach(mode => {
      allModes.push({
        ...mode,
        category: category.name,
        categoryId: category.id,
      });
    });
  });
  return allModes;
};

// Get templates for a mode
export const getTemplatesForMode = (modeId) => {
  return MODE_TEMPLATES[modeId] || MODE_TEMPLATES['general'];
};
